/**
 * Shared controller for editorial promo-bar sections.
 *
 * The same section can appear more than once in a header group. Keep its
 * behavior isolated per root, but register the controller and Theme Editor
 * lifecycle listeners once per page.
 */
(function () {
  'use strict';

  if (window.themeEditorialPromoBar && typeof window.themeEditorialPromoBar.init === 'function') {
    window.themeEditorialPromoBar.init(document);
    return;
  }

  var rootSelector = '[data-editorial-promo-bar]';
  var stateByRoot = new WeakMap();

  function rootsIn(scope) {
    if (!scope) return [];

    var roots = [];
    if (scope.nodeType === 1 && scope.matches && scope.matches(rootSelector)) {
      roots.push(scope);
    }

    if (scope.querySelectorAll) {
      Array.prototype.forEach.call(scope.querySelectorAll(rootSelector), function (root) {
        roots.push(root);
      });
    }

    return roots;
  }

  function initRoot(root) {
    if (!root || stateByRoot.has(root)) return;

    root.dataset.initialized = 'true';

    var daysEl = root.querySelector('[data-days]');
    var hoursEl = root.querySelector('[data-hours]');
    var minutesEl = root.querySelector('[data-minutes]');
    var secondsEl = root.querySelector('[data-seconds]');
    var hasCountdown = Boolean(daysEl || hoursEl || minutesEl || secondsEl);
    var messages = Array.prototype.slice.call(root.querySelectorAll('[data-promo-message]'));
    var prevButton = root.querySelector('[data-promo-prev]');
    var nextButton = root.querySelector('[data-promo-next]');
    var primary = root.querySelector('.editorial-promo-bar__primary');
    var promo = root.querySelector('.editorial-promo-bar__promo');
    var promoRail = root.querySelector('.editorial-promo-bar__promo-rail');
    var promoTrack = root.querySelector('[data-promo-track]');
    var promoGroup = root.querySelector('[data-promo-group]');
    var promoDisplay = root.dataset.promoDisplay || 'rotate';
    var marqueeGroupCount = 1;
    var marqueeBaseDuration = parseFloat(window.getComputedStyle(root).getPropertyValue('--epb-marquee-duration')) || 22;
    var activeIndex = 0;
    var rotateTimer = null;
    var countdownTimer = null;
    var fitFrame = null;
    var promoFitFrame = null;
    var marqueeFrame = null;
    var resizeObserver = null;
    var usesResizeFallback = false;
    var countdownState = getCountdownState();
    var remainingSeconds = countdownState.remainingSeconds;
    var cycleSeconds = countdownState.cycleSeconds;
    var countdownBehavior = countdownState.behavior;

    function pad(value) {
      return String(value).padStart(2, '0');
    }

    function fitPrimaryToWidth() {
      fitFrame = null;
      if (!primary) return;

      root.style.setProperty('--epb-primary-scale', '1');

      var availableWidth = primary.parentElement ? primary.parentElement.clientWidth : root.clientWidth;
      if (!availableWidth) return;

      var requiredWidth = primary.scrollWidth;
      if (requiredWidth <= availableWidth) return;

      var scale = Math.max(0.68, Math.min(1, (availableWidth - 2) / requiredWidth));
      root.style.setProperty('--epb-primary-scale', scale.toFixed(3));
    }

    function scheduleFitPrimary() {
      if (fitFrame) window.cancelAnimationFrame(fitFrame);
      fitFrame = window.requestAnimationFrame(fitPrimaryToWidth);
    }

    function fitPromoToWidth() {
      promoFitFrame = null;
      if (!promo || messages.length === 0 || promoDisplay === 'marquee') return;

      root.style.setProperty('--epb-promo-scale', '1');

      var activeMessage = messages[activeIndex] || messages[0];
      var availableWidth = promo.clientWidth;
      if (!activeMessage || !availableWidth) return;

      var requiredWidth = activeMessage.scrollWidth;
      if (requiredWidth <= availableWidth) return;

      var scale = Math.max(0.68, Math.min(1, (availableWidth - 2) / requiredWidth));
      root.style.setProperty('--epb-promo-scale', scale.toFixed(3));
    }

    function scheduleFitPromo() {
      if (promoFitFrame) window.cancelAnimationFrame(promoFitFrame);
      promoFitFrame = window.requestAnimationFrame(fitPromoToWidth);
    }

    function scheduleFitLayout() {
      scheduleFitPrimary();
      scheduleFitPromo();
    }

    function getCountdownState() {
      var values = String(root.dataset.countdownConfig || '').split('|');
      var configuredRemainingSeconds = parseInt(values[0], 10);
      var configuredCycleSeconds = parseInt(values[1], 10);
      var configuredBehavior = values[2] || 'r';
      var renderedAt = parseInt(values[3], 10);

      if (!Number.isFinite(configuredCycleSeconds) || configuredCycleSeconds <= 0) {
        configuredCycleSeconds = 172800;
      }

      if (!Number.isFinite(configuredRemainingSeconds)) {
        configuredRemainingSeconds = configuredCycleSeconds;
      }

      if (Number.isFinite(renderedAt)) {
        var elapsedSinceRender = Math.max(0, Math.floor(new Date().getTime() / 1000) - renderedAt);
        configuredRemainingSeconds -= elapsedSinceRender;
      }

      return {
        remainingSeconds: configuredRemainingSeconds,
        cycleSeconds: configuredCycleSeconds,
        behavior: configuredBehavior,
      };
    }

    function normalizeRemainingSeconds(value) {
      if (countdownBehavior !== 'r') return value;

      while (value <= 0) {
        value += cycleSeconds;
      }
      return value;
    }

    function updateCountdown() {
      if (remainingSeconds <= 0) {
        if (countdownBehavior === 'h') {
          root.classList.add('is-expired');
          if (countdownTimer) window.clearInterval(countdownTimer);
          return;
        }

        if (countdownBehavior === 'r') {
          remainingSeconds = normalizeRemainingSeconds(remainingSeconds);
        } else {
          remainingSeconds = 0;
          if (countdownTimer) window.clearInterval(countdownTimer);
        }
      }

      var secondsTotal = Math.max(0, Math.floor(remainingSeconds));
      var days = Math.floor(secondsTotal / 86400);
      var hours = Math.floor((secondsTotal % 86400) / 3600);
      var minutes = Math.floor((secondsTotal % 3600) / 60);
      var seconds = secondsTotal % 60;

      if (daysEl) daysEl.textContent = pad(days);
      if (hoursEl) hoursEl.textContent = pad(hours);
      if (minutesEl) minutesEl.textContent = pad(minutes);
      if (secondsEl) secondsEl.textContent = pad(seconds);

      remainingSeconds -= 1;
    }

    function showMessage(index, direction) {
      if (messages.length === 0) return;
      root.dataset.promoDirection = direction || 'next';
      activeIndex = (index + messages.length) % messages.length;
      messages.forEach(function (message, messageIndex) {
        message.classList.toggle('is-active', messageIndex === activeIndex);
      });
      scheduleFitPromo();
    }

    function stopRotation() {
      if (rotateTimer) window.clearInterval(rotateTimer);
      rotateTimer = null;
    }

    function startRotation() {
      var speed = parseInt(root.dataset.rotationSpeed, 10);
      var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (promoDisplay !== 'rotate' || root.dataset.autoRotate !== 'true' || messages.length < 2 || reducedMotion) return;
      rotateTimer = window.setInterval(function () {
        showMessage(activeIndex + 1, 'next');
      }, Math.max(speed, 2) * 1000);
    }

    function appendMarqueeGroup() {
      var duplicate = promoGroup.cloneNode(true);
      duplicate.removeAttribute('data-promo-group');
      duplicate.setAttribute('data-editorial-promo-marquee-clone', '');
      duplicate.querySelectorAll('[data-promo-message]').forEach(function (message) {
        message.removeAttribute('data-promo-message');
      });
      duplicate.setAttribute('aria-hidden', 'true');
      promoTrack.appendChild(duplicate);
      marqueeGroupCount += 1;
    }

    function ensureMarqueeCoverage() {
      if (promoDisplay !== 'marquee' || !promoRail || !promoTrack || !promoGroup || messages.length === 0) return;

      var groupWidth = promoGroup.getBoundingClientRect().width;
      var railWidth = promoRail.clientWidth;
      if (!groupWidth || !railWidth) return;

      var minimumGroupCount = Math.max(2, Math.ceil((railWidth * 2) / groupWidth));
      if (minimumGroupCount % 2 !== 0) minimumGroupCount += 1;

      while (marqueeGroupCount < minimumGroupCount) {
        appendMarqueeGroup();
      }

      root.style.setProperty('--epb-marquee-duration', (marqueeBaseDuration * (marqueeGroupCount / 2)) + 's');
    }

    function onPreviousClick() {
      stopRotation();
      showMessage(activeIndex - 1, 'previous');
      startRotation();
    }

    function onNextClick() {
      stopRotation();
      showMessage(activeIndex + 1, 'next');
      startRotation();
    }

    function destroy() {
      stopRotation();
      if (countdownTimer) window.clearInterval(countdownTimer);
      if (fitFrame) window.cancelAnimationFrame(fitFrame);
      if (promoFitFrame) window.cancelAnimationFrame(promoFitFrame);
      if (marqueeFrame) window.cancelAnimationFrame(marqueeFrame);

      if (resizeObserver) resizeObserver.disconnect();
      if (usesResizeFallback) window.removeEventListener('resize', scheduleFitLayout);
      if (promoDisplay === 'marquee') window.removeEventListener('resize', ensureMarqueeCoverage);
      if (prevButton) prevButton.removeEventListener('click', onPreviousClick);
      if (nextButton) nextButton.removeEventListener('click', onNextClick);

      if (promoTrack) {
        Array.prototype.forEach.call(promoTrack.querySelectorAll('[data-editorial-promo-marquee-clone]'), function (duplicate) {
          duplicate.remove();
        });
      }

      if (root._editorialPromoResizeObserver === resizeObserver) {
        delete root._editorialPromoResizeObserver;
      }
      delete root.dataset.initialized;
    }

    stateByRoot.set(root, { destroy: destroy });

    if (promoDisplay === 'marquee' && promoTrack && promoGroup && messages.length > 0) {
      ensureMarqueeCoverage();
      marqueeFrame = window.requestAnimationFrame(ensureMarqueeCoverage);
      window.addEventListener('resize', ensureMarqueeCoverage);
    }

    if (promoDisplay === 'marquee' || messages.length < 2) {
      if (prevButton) prevButton.hidden = true;
      if (nextButton) nextButton.hidden = true;
    }

    if (prevButton) prevButton.addEventListener('click', onPreviousClick);
    if (nextButton) nextButton.addEventListener('click', onNextClick);

    if (hasCountdown) {
      updateCountdown();
      countdownTimer = window.setInterval(updateCountdown, 1000);
    }
    scheduleFitLayout();
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(scheduleFitLayout);
    }
    startRotation();

    if ('ResizeObserver' in window) {
      resizeObserver = new ResizeObserver(scheduleFitLayout);
      root._editorialPromoResizeObserver = resizeObserver;
      resizeObserver.observe(root);
    } else {
      usesResizeFallback = true;
      window.addEventListener('resize', scheduleFitLayout);
    }
  }

  function init(scope) {
    rootsIn(scope || document).forEach(initRoot);
  }

  function destroy(scope) {
    rootsIn(scope || document).forEach(function (root) {
      var state = stateByRoot.get(root);
      if (!state) return;

      state.destroy();
      stateByRoot.delete(root);
    });
  }

  window.themeEditorialPromoBar = {
    init: init,
    destroy: destroy
  };

  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });

  document.addEventListener('shopify:section:unload', function (event) {
    destroy(event.target);
  });

  init(document);
})();
